import React from 'react';
import { Typography, Container, Link, Box, IconButton } from '@mui/material';
import { Grid2 } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { styled } from '@mui/system';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';

const StyledFooter = styled('footer')(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: '#FFFFFF',
    padding: theme.spacing(6, 0, 4, 0), // Added bottom padding
    marginTop: 'auto',
}));

const StyledLink = styled(Link)(({ theme }) => ({
    color: '#FFFFFF',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
        color: theme.palette.secondary.light,
        textDecoration: 'underline',
    },
}));

const StyledTypography = styled(Typography)({
    color: '#FFFFFF',
});

const AppDownloadButton = styled(IconButton)(({ theme }) => ({
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#FFFFFF',
    margin: theme.spacing(0.5),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    transition: 'all 0.3s ease',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    },
}));

const AppDownloadSection = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 16,
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
}));

const FooterSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    height: '100%', // Ensure full height usage
    display: 'flex',
    flexDirection: 'column',
    '& a': {
        display: 'block',
        marginBottom: theme.spacing(1),
        fontSize: '0.9rem',
    },
}));

const FooterGrid = styled(Grid2)(({ theme }) => ({
    // Ensure equal height columns on desktop
    [theme.breakpoints.up('md')]: {
        alignItems: 'stretch',
        '& > *': {
            display: 'flex',
            flexDirection: 'column',
        },
    },
}));

function Footer() {
    const handleAppDownload = (platform) => {
        const urls = {
            ios: 'https://apps.apple.com/us/app/getborrowed/id6741023320',
            android: 'https://play.google.com/store/apps/details?id=com.getborrowed.app'
        };

        window.open(urls[platform], '_blank');
    };

    return (
        <StyledFooter>
            <Container maxWidth="lg">
                {/* App Download Section */}
                <AppDownloadSection>
                    <StyledTypography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Find what you need, lend what you don't use.
                    </StyledTypography>
                    <StyledTypography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                        Get the app for the best experience
                    </StyledTypography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <AppDownloadButton
                            onClick={() => handleAppDownload('ios')}
                            title="Download for iOS"
                        >
                            <AppleIcon sx={{ fontSize: 28 }} />
                        </AppDownloadButton>
                        <AppDownloadButton
                            onClick={() => handleAppDownload('android')}
                            title="Download for Android"
                        >
                            <AndroidIcon sx={{ fontSize: 28 }} />
                        </AppDownloadButton>
                    </Box>
                </AppDownloadSection>

                {/* Main Footer Content */}
                <FooterGrid container spacing={4} justifyContent="space-between">
                    <Grid2 item xs={12} sm={6} md={3}>
                        <FooterSection>
                            <StyledTypography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                                Borrowed
                            </StyledTypography>
                            <StyledTypography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                                The peer-to-peer sharing platform that connects people and builds sustainable communities.
                            </StyledTypography>
                        </FooterSection>
                    </Grid2>

                    <Grid2 item xs={12} sm={6} md={2}>
                        <FooterSection>
                            <StyledTypography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                Platform
                            </StyledTypography>
                            <StyledLink component={RouterLink} to="/how-it-works" variant="body2">
                                How It Works
                            </StyledLink>
                            <StyledLink component={RouterLink} to="/listings" variant="body2">
                                Browse Items
                            </StyledLink>
                            <StyledLink component={RouterLink} to="/community-guidelines" variant="body2">
                                Safety Tips
                            </StyledLink>
                            <StyledLink component={RouterLink} to="/how-it-works" variant="body2">
                                Pricing
                            </StyledLink>
                        </FooterSection>
                    </Grid2>

                    <Grid2 item xs={12} sm={6} md={2}>
                        <FooterSection>
                            <StyledTypography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                Company
                            </StyledTypography>
                            <StyledLink component={RouterLink} to="/about" variant="body2">
                                About Us
                            </StyledLink>
                            {/* <StyledLink component={RouterLink} to="/careers" variant="body2">
                                Careers
                            </StyledLink>
                            <StyledLink component={RouterLink} to="/blog" variant="body2">
                                Blog
                            </StyledLink>
                            <StyledLink component={RouterLink} to="/press" variant="body2">
                                Press
                            </StyledLink> */}
                        </FooterSection>
                    </Grid2>

                    <Grid2 item xs={12} sm={6} md={2}>
                        <FooterSection>
                            <StyledTypography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                Support
                            </StyledTypography>
                            <StyledLink component={RouterLink} to="/faq" variant="body2">
                                Frequently Asked Questions
                            </StyledLink>
                            <StyledLink href="mailto:support@borrowed.com" variant="body2">
                                Contact Us
                            </StyledLink>
                            <StyledLink component={RouterLink} to="/community-guidelines" variant="body2">
                                Community
                            </StyledLink>
                            {/* <StyledLink component={RouterLink} to="/feedback" variant="body2">
                                Feedback
                            </StyledLink> */}
                        </FooterSection>
                    </Grid2>

                    <Grid2 item xs={12} sm={6} md={3}>
                        <FooterSection>
                            <StyledTypography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                Legal
                            </StyledTypography>
                            <StyledLink component={RouterLink} to="/terms" variant="body2">
                                Terms of Service
                            </StyledLink>
                            <StyledLink component={RouterLink} to="/privacy" variant="body2">
                                Privacy Policy
                            </StyledLink>
                            {/* <StyledLink component={RouterLink} to="/dispute-resolution" variant="body2">
                                Dispute Resolution
                            </StyledLink> */}
                        </FooterSection>
                    </Grid2>
                </FooterGrid>

                {/* Bottom Section */}
                <Box
                    sx={{
                        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                        pt: 3,
                        mt: 4,
                        pb: 2, // Added bottom padding for the copyright section
                        textAlign: 'center',
                    }}
                >
                    <StyledTypography variant="body2" sx={{ opacity: 0.8 }}>
                        © {new Date().getFullYear()} Borrowed. All rights reserved. Made with ❤️ for sustainable communities.
                    </StyledTypography>
                </Box>
            </Container>
        </StyledFooter>
    );
}

export default Footer;